<template>
  <div ref="chartRoot" id="stage-parent" class="bar-chart">
    <v-stage :config="configKonva">
      <v-layer>
        <v-text v-for="(t, index) in yAxisLabels" :key="'t' + index" :config="t"></v-text>
        <v-line :config="yAxis" />
        <v-line v-for="(x, index) in yGrid" :config="x" :key="'r' + index" />
        <v-rect :config="lightBar"></v-rect>
        <v-rect :config="darkBar"></v-rect>
        <BarChartMarker
          :markerHeight="markerHeight"
          dir="LEFT"
          v-for="(item, index) in rightMarker"
          :key="'bc' + index"
          :isSelected="item.isSelected"
          :name="item.name"
          :displayValue="item.rawValue"
          :from="{ x: 90, y: item.tunedValue }"
          :to="{ x: 170, y: item.value }"
          :color="item.color"
          @marker-hover="onMarkerHover"
          @marker-leave="onMarkerLeave"
        ></BarChartMarker>
        <BarChartMarker
          :markerHeight="markerHeight"
          dir="RIGHT"
          v-for="(item, index) in leftMarkers"
          :key="'lc' + index"
          :isSelected="item.isSelected"
          :name="item.name"
          :displayValue="item.rawValue"
          :from="{ x: 250, y: item.tunedValue }"
          :to="{ x: 220, y: item.value }"
          :color="item.color"
          @marker-hover="onMarkerHover"
          @marker-leave="onMarkerLeave"
        ></BarChartMarker>
      </v-layer>
    </v-stage>
    <div
        class="bar-chart__title text-xs font-medium text-center pl-12 mt-1 text-gray-600 dark:text-gray-300"
        :style="{ width: width + 'px' }"
    >{{ propertyName }}</div>
    <div
      v-show="hoverTip"
      class="bar-chart__tip"
      role="tooltip"
      :style="hoverTipStyle"
    >
      <div class="bar-chart__tip-row">
        <span class="bar-chart__tip-swatch" :style="{ background: hoverTip && hoverTip.color }"></span>
        <span class="bar-chart__tip-name">{{ hoverTip && hoverTip.name }}</span>
      </div>
      <div class="bar-chart__tip-value">
        <span class="bar-chart__tip-label">Value</span>
        <strong>{{ hoverTip && hoverTip.value !== '' ? hoverTip.value : '—' }}</strong>
      </div>
      <div v-if="propertyName" class="bar-chart__tip-prop">{{ propertyName }}</div>
    </div>
  </div>
</template>
<script>
import BarChartMarker from "@/components/Predix/BarChartMarker.vue";
import { smartPruneNumber } from "@/utils/number.js";
const STEP_COUNT = 8;
const Y_AXIS_START_X = 80;
const BAR_LEFT_PADDING = 180;
const BAR_WIDTH = 35;
// const RIGHT_MARKER_START=90
export default {
  name: "BarChart",
  components: { BarChartMarker },
  data: () => ({
    sceneWidth: 1000,
    sceneHeight: 1000,

    markerHeight: 22,
    hoverTip: null,
    // stepHeight: 50,
    // stepCount: 8,
    // yAxisStartX: 80,
  }),
  props: {
    propertyName: {
      type: String,
      default: " PhaseComposition Fe wt% FCC_A1[Al]   ",
    },
    propertyValue: {
      type: Number,
      default: 100,
    },
    minValue: {
      type: Number,
      default: 36,
    },
    maxValue: {
      type: Number,
      default: 180,
    },
    avgValue: {
      type: Number,
      default: 38,
    },
    standardAlloys: {
      type: Set,
      default: () => null,
    },
    minColor: {
      type: String,
      default: "#fee2e2",
    },
    maxColor: {
      type: String,
      default: "#dcfce7",
    },
    avgColor: {
      type: String,
      default: "#fef9c3",
    },
    selectedColor: {
      type: String,
      default: "#0d9488",
    },
    colors: {
      type: Array,
      default: () => [
        { dark: "#D88972", light: "#FEAD94" },
        { dark: "#E9A643", light: "#FFC671" },
        { dark: "#A47CD8", light: "#CAA6F8" },
        { dark: "#BFD08D", light: "#DFEFAF" },
        { dark: "#CBB086", light: "#E8D4B4" },
        { dark: "#AA8FC1", light: "#C4AFD6" },
        { dark: "#EEC4A2", light: "#FCE2CD" },
        { dark: "#97C9DA", light: "#C7E7F2" },
        { dark: "#C585B7", light: "#E3A0D5" },
        { dark: "#DAAFCD", light: "#F4D9EC" },
        { dark: "#93A4DE", light: "#93A4DE" },
        { dark: "#DFC785", light: "#F1DFAD" },
        { dark: "#689879", light: "#92BDA1" },
      ],
    },
    height: {
      type: Number,
      default: 350,
    },
    width: {
      type: Number,
      default: 400,
    },
  },
  computed: {
    hoverTipStyle() {
      if (!this.hoverTip) return { display: "none" };
      const offset = 14;
      let left = this.hoverTip.x + offset;
      let top = this.hoverTip.y + offset;
      const tipW = 180;
      const tipH = 78;
      if (typeof window !== "undefined") {
        if (left + tipW > window.innerWidth - 8) {
          left = this.hoverTip.x - tipW - offset;
        }
        if (top + tipH > window.innerHeight - 8) {
          top = this.hoverTip.y - tipH - 8;
        }
        left = Math.max(8, left);
        top = Math.max(8, top);
      }
      return {
        left: `${left}px`,
        top: `${top}px`,
      };
    },
    /** Konva colors aligned with AnalyticsChart.vue (light / dark flat fills + teal selected accent) */
    chartSurface() {
      const dark = this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark;
      if (dark) {
        return {
          labelFill: "#9ca3af",
          axisStroke: "#4b5563",
          gridStroke: "#374151",
          trackBar: "#1f2937",
          trackStroke: "#374151",
          valueBar: "#2dd4bf",
        };
      }
      return {
        labelFill: "#6b7280",
        axisStroke: "#d1d5db",
        gridStroke: "#e5e7eb",
        trackBar: "#f9fafb",
        trackStroke: "#e5e7eb",
        valueBar: "#0d9488",
      };
    },
    configKonva() {
      return {
        width: this.width,
        height: this.height + 10,
      };
    },
    leftMarkers() {
      const x = Array.from(this.standardAlloys || []).map((item, index) => ({
        name: item.name,
        value: item.value,
        isSelected: false,
        color: this.colors[index % this.colors.length].light,
      }));
      return this.rescaleMarkers(x);
    },
    rightMarker() {
      return this.rescaleMarkers([
        { name: "Min", value: this.minValue, color: this.minColor, isSelected: false },
        { name: "Max", value: this.maxValue, color: this.maxColor, isSelected: false },
        { name: "Avg", value: this.avgValue, color: this.avgColor, isSelected: false },
        { name: "Predicted", value: this.propertyValue, color: this.selectedColor, isSelected: true },
      ]);
    },
    stepHeight() {
      return Math.round(this.height / STEP_COUNT);
    },
    yMin() {
      if (this.standardAlloys.size > 0) {
        return Math.min(this.minValue, this.propertyValue, ...Array.from(this.standardAlloys).map((item) => item.value));
      } else {
        return Math.min(this.minValue, this.propertyValue);
      }
    },
    yMax() {
      if (this.standardAlloys.size > 0) {
        return Math.max(this.maxValue, this.propertyValue, ...Array.from(this.standardAlloys).map((item) => item.value));
      } else {
        return Math.max(this.maxValue, this.propertyValue);
      }
    },
    yAxisLabels() {
      const stepSize = (this.yMax - this.yMin) / STEP_COUNT;
      let res = [];
      for (var i = 0; i < STEP_COUNT + 1; i++) {
        res.push({
          x: 0,
          y: (STEP_COUNT - i) * (this.stepHeight - 1),
          text: String(smartPruneNumber(this.yMin + i * stepSize)),
          fontSize: 12,
          fontFamily: "Inter",
          fill: this.chartSurface.labelFill,
          width: 70,
          padding: 0,
          align: "right",
        });
      }
      return res;
    },
    yAxis() {
      return {
        x: 0,
        y: 0,
        points: [Y_AXIS_START_X, 0, Y_AXIS_START_X, this.stepHeight * STEP_COUNT],
        stroke: this.chartSurface.axisStroke,
        strokeWidth: 1,
      };
    },
    yGrid() {
      let res = [];
      for (var i = 0; i < STEP_COUNT + 1; i++) {
        res.push({
          x: 0,
          y: 0,
          points: [Y_AXIS_START_X, i * this.stepHeight, 500, i * this.stepHeight],
          stroke: this.chartSurface.gridStroke,
          strokeWidth: 1,
          dash: i == STEP_COUNT ? null : [4, 4],
          opacity: i === STEP_COUNT ? 1 : 0.85,
        });
      }
      return res;
    },
    lightBar() {
      return {
        x: BAR_LEFT_PADDING,
        y: 0,
        width: BAR_WIDTH,
        height: this.height + 1,
        fill: this.chartSurface.trackBar,
        stroke: this.chartSurface.trackStroke,
        strokeWidth: 1,
        cornerRadius: 6,
      };
    },
    darkBar() {
      const h = this.getRealH(this.propertyValue);
      return {
        x: BAR_LEFT_PADDING,
        y: this.height - h,
        width: BAR_WIDTH,
        height: h + 1,
        fill: this.chartSurface.valueBar,
        strokeWidth: 0,
        cornerRadius: 6,
      };
    },
  },
  methods: {
    onMarkerHover(payload) {
      this.hoverTip = payload;
    },
    onMarkerLeave() {
      this.hoverTip = null;
    },
    rescaleMarkers(items) {
      if (!items || items.length === 0) {
        return [];
      }
      // Keep rawValue for labels; `value` becomes pixel Y on the chart.
      let x = items
        .map((item) => ({
          ...item,
          rawValue: item.value,
        }))
        .sort((a, b) => a.rawValue - b.rawValue)
        .map((item) => {
          const pixelY = this.height - this.getRealH(item.rawValue);
          const bucketIndex = Math.floor(pixelY / (this.markerHeight + 1)) + 1;
          return {
            ...item,
            value: pixelY,
            bucketIndex,
            tunedValue: bucketIndex * this.markerHeight,
          };
        });
      x.forEach((item, index, arr) => {
        if (index > 0 && arr.slice(0, index).findIndex((m) => m.bucketIndex == item.bucketIndex) != -1) {
          item.bucketIndex = Math.min(...arr.slice(0, index).map((c) => c.bucketIndex)) - 1;
          item.tunedValue = item.bucketIndex * this.markerHeight;
        }
      });
      const minBucketIndex = x[x.length - 1].bucketIndex;
      if (minBucketIndex <= 0) {
        for (var j = x.length - 1; j >= 0; j--) {
          x[j].bucketIndex = x[j].bucketIndex + Math.abs(minBucketIndex) + 1;
          x[j].tunedValue = x[j].bucketIndex * this.markerHeight;
          if (j == 0 || x[j].bucketIndex < x[j - 1].bucketIndex) {
            break;
          }
        }
      }

      return x;
    },
    getRealH(val) {
      // return this.height * (this.yMax - val - this.yMin)/(this.yMax - this.yMin)
      return (this.height * (val - this.yMin)) / (this.yMax - this.yMin);
    },
  },
};

//RESPONSIVE https://konvajs.org/docs/sandbox/Responsive_Canvas.html
</script>

<style scoped>
.bar-chart {
  position: relative;
  font-family: var(--font-inter, "Inter"), var(--font-sans, ui-sans-serif, system-ui, sans-serif);
}

.bar-chart__tip {
  position: fixed;
  z-index: 4000;
  min-width: 9.5rem;
  max-width: 16rem;
  padding: 0.65rem 0.8rem;
  border-radius: 0.65rem;
  border: 1px solid rgba(229, 231, 235, 0.95);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 4px 6px -4px rgba(0, 0, 0, 0.08);
  pointer-events: none;
  text-align: left;
}

.bar-chart__tip-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.35rem;
}

.bar-chart__tip-swatch {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 9999px;
  flex-shrink: 0;
}

.bar-chart__tip-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.bar-chart__tip-value {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: #111827;
}

.bar-chart__tip-label {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.bar-chart__tip-value strong {
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}

.bar-chart__tip-prop {
  margin-top: 0.4rem;
  padding-top: 0.35rem;
  border-top: 1px solid #f3f4f6;
  font-size: 0.6875rem;
  line-height: 1.35;
  color: #6b7280;
}
</style>

<style lang="scss">
/* Match AnalyticsChart.vue typography on html.dark + Vuetify theme (caption may sit outside Tailwind dark: in some shells) */
.v-application.theme--light .bar-chart .bar-chart__title {
  color: #6b7280;
}

.v-application.theme--dark .bar-chart .bar-chart__title {
  color: #9ca3af;
}

html.dark .bar-chart .bar-chart__title {
  color: #9ca3af;
}

html.dark .bar-chart .bar-chart__tip,
.v-application.theme--dark .bar-chart .bar-chart__tip {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(31, 41, 55, 0.98);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.45);
}

html.dark .bar-chart .bar-chart__tip-name,
.v-application.theme--dark .bar-chart .bar-chart__tip-name,
html.dark .bar-chart .bar-chart__tip-value,
.v-application.theme--dark .bar-chart .bar-chart__tip-value {
  color: #f3f4f6;
}

html.dark .bar-chart .bar-chart__tip-label,
.v-application.theme--dark .bar-chart .bar-chart__tip-label,
html.dark .bar-chart .bar-chart__tip-prop,
.v-application.theme--dark .bar-chart .bar-chart__tip-prop {
  color: #9ca3af;
}

html.dark .bar-chart .bar-chart__tip-prop,
.v-application.theme--dark .bar-chart .bar-chart__tip-prop {
  border-top-color: rgba(255, 255, 255, 0.1);
}
</style>
